import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { AlertTriangle, Check, X } from 'lucide-react';

/**
 * MissedPrayerDialog
 * Shown when user taps a prayer that passed >30 min ago and wasn't marked.
 * Props:
 *   open        — boolean
 *   prayer      — prayer key e.g. "Fajr"
 *   prayerTime  — formatted time string e.g. "5:14 AM"
 *   onPrayedOnTime — () => void  — user says they prayed on time
 *   onMissedIt     — () => void  — user says they missed it (Qaza)
 *   onClose        — () => void
 */
const MissedPrayerDialog = ({ open, prayer, prayerTime, onPrayedOnTime, onMissedIt, onClose }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();

  if (!open) return null;

  const translatedPrayer = t(`prayers.${prayer}`, prayer);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className={`max-w-sm mx-auto rounded-2xl border-0 shadow-2xl p-0 overflow-hidden ${
          isDarkMode ? 'bg-[#1e293b] text-white' : 'bg-white text-gray-900'
        }`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header strip */}
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 pt-6 pb-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <DialogHeader className="text-left p-0 space-y-0">
              <DialogTitle className={`text-base font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('qaza.dialogTitle', { prayer: translatedPrayer })}
              </DialogTitle>
              <DialogDescription className={`text-sm mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {prayerTime && <span className="font-medium text-amber-500">{prayerTime} · </span>}
                {t('qaza.dialogSubtitle')}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Buttons */}
        <div className="px-6 py-4 space-y-3">
          {/* Yes — prayed on time */}
          <button
            data-testid="dialog-prayed-on-time"
            onClick={onPrayedOnTime}
            className="w-full flex items-center gap-3 p-4 rounded-xl transition-all bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
          >
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4" />
            </div>
            <span className="flex-1 text-left">{t('qaza.prayedOnTime')}</span>
          </button>

          {/* No — missed it (Qaza) */}
          <button
            data-testid="dialog-missed-it"
            onClick={onMissedIt}
            className={`w-full flex items-center gap-3 p-4 rounded-xl transition-all font-medium border ${
              isDarkMode
                ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
                : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? 'bg-red-500/20' : 'bg-red-100'
            }`}>
              <X className="w-4 h-4" />
            </div>
            <span className="flex-1 text-left">{t('qaza.missedIt')}</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MissedPrayerDialog;
